import { Container, Grid, Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { UseFormReturnType } from '@mantine/form/lib/use-form';

import { showNotification } from '@mantine/notifications';
import React, { ReactNode, useEffect } from 'react';
import { X } from 'tabler-icons-react';
import { UserManagementFilterMode } from '../../enums/misc.enum';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import { BasicMerchant } from '../../type/admin.types';
import { ErrorResponse } from '../../type/error-response.types';
import {
  SelectData,
  UserManagementFilterData,
  UserManagementFilterForm,
} from '../../type/filter.types';
import { BasicBranch, BasicPos } from '../../type/select.types';

const ResponsiveCol = ({ children }: { children: ReactNode }) => {
  return (
    <Grid.Col xs={12} sm={6}>
      {children}
    </Grid.Col>
  );
};

interface Props {
  filterValues: UseFormReturnType<UserManagementFilterForm>;
  setIsLoadMerchants: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadMerchants: boolean;
  filterSelectData: UseFormReturnType<UserManagementFilterData>;
  mode?: UserManagementFilterMode;
}

function ResellerPaymentLinkFilter(props: Props) {
  const {
    filterValues,
    setIsLoadMerchants,
    isLoadMerchants,
    filterSelectData,
    mode,
  } = props;
  const axiosPrivate = useAxiosPrivate();

  const selectLoadingData = useForm({
    initialValues: {
      isPartnersLoading: false,
      isResellersLoading: false,
      isMerchantsLoading: false,
      isBranchesLoading: false,
      isPosLoading: false,
    },
  });

  const findValueInData = (data: SelectData[], value: string): boolean => {
    for (let i = 0; i < data?.length; i++) {
      if (data[i].value === value) {
        return true;
      }
    }
    return false;
  };

  const noData = 'No data';
  const loadingData = 'Loading...';

  useEffect(() => {
    const loadMerchants = async () => {
      selectLoadingData.setFieldValue('isMerchantsLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/merchants');

        const merchants = response.data?.items?.map(
          (merchant: BasicMerchant) => {
            return {
              label: merchant.merchantName,
              value: merchant.merchantId.toString(),
            };
          }
        );

        filterSelectData.setFieldValue('merchantsData', merchants);
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-merchants-data',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isMerchantsLoading', false);
      }
    };

    if (isLoadMerchants) {
      loadMerchants();
    } else {
      setIsLoadMerchants(true);
    }
  }, []);

  useDidMountEffect(() => {
    let ignore = false;

    const loadMerchantBranches = async () => {
      selectLoadingData.setFieldValue('isBranchesLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/branches', {
          params: {
            merchantId: filterValues.values.merchant,
          },
        });

        const merchantBranches = response.data?.items?.map(
          (merchant: BasicBranch) => {
            return {
              label: merchant.branchName,
              value: merchant.branchId.toString(),
            };
          }
        );

        if (!ignore) {
          const found = findValueInData(
            merchantBranches,
            filterValues.values.merchantBranch
          );

          if (!found) {
            filterValues.setFieldValue('merchantBranch', '');
          }
          filterSelectData.setFieldValue(
            'merchantBranchesData',
            merchantBranches
          );
        }
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-branches-data',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isBranchesLoading', false);
      }
    };

    if (filterValues.values.merchant) {
      loadMerchantBranches();
    } else {
      filterValues.setFieldValue('merchantBranch', '');
      filterSelectData.setFieldValue('merchantBranchesData', []);
    }

    return () => {
      ignore = true;
    };
  }, [filterValues.values.merchant]);

  useDidMountEffect(() => {
    let ignore = false;

    const loadPos = async () => {
      selectLoadingData.setFieldValue('isPosLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/pos', {
          params: {
            partnerId: filterValues.values.partner,
            resellerId: filterValues.values.reseller,
            merchantId: filterValues.values.merchant,
            branchId: filterValues.values.merchantBranch,
          },
        });

        const pos = response.data?.items?.map((merchant: BasicPos) => {
          return {
            label: merchant.posName,
            value: merchant.posId.toString(),
          };
        });

        if (!ignore) {
          const found = findValueInData(pos, filterValues.values.pos || '');

          if (!found) {
            filterValues.setFieldValue('pos', '');
          }
          filterSelectData.setFieldValue('posData', pos);
        }
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-pos-data',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isPosLoading', false);
      }
    };

    if (filterValues.values.merchantBranch) {
      loadPos();
    } else {
      filterValues.setFieldValue('pos', '');
      filterSelectData.setFieldValue('posData', []);
    }

    return () => {
      ignore = true;
    };
  }, [filterValues.values.merchantBranch]);

  return (
    <>
      <Container fluid p={0} m={0}>
        <Grid>
          <ResponsiveCol>
            <Select
              label='Select Merchant'
              data={
                selectLoadingData.values.isMerchantsLoading
                  ? [loadingData]
                  : filterSelectData.values.merchantsData?.length === 0
                  ? [noData]
                  : filterSelectData.values.merchantsData
              }
              value={
                selectLoadingData.values.isMerchantsLoading
                  ? loadingData
                  : filterSelectData.values.merchantsData?.length === 0
                  ? noData
                  : filterValues.values.merchant
              }
              disabled={
                selectLoadingData.values.isMerchantsLoading ||
                filterSelectData.values.merchantsData?.length === 0
              }
              onChange={(value: string) =>
                filterValues.setFieldValue('merchant', value)
              }
              clearable={
                !(
                  selectLoadingData.values.isMerchantsLoading ||
                  filterSelectData.values.merchantsData?.length === 0
                )
              }
              searchable
            />
          </ResponsiveCol>
          {mode !== UserManagementFilterMode.APPROVE_ENTITY_MODE && (
            <ResponsiveCol>
              <Select
                label='Select Merchant Branch'
                data={
                  selectLoadingData.values.isBranchesLoading
                    ? [loadingData]
                    : filterSelectData.values.merchantBranchesData?.length ===
                        0 && filterValues.values.merchant
                    ? [noData]
                    : filterSelectData.values.merchantBranchesData
                }
                value={
                  selectLoadingData.values.isBranchesLoading
                    ? loadingData
                    : filterSelectData.values.merchantBranchesData?.length ===
                        0 && filterValues.values.merchant
                    ? noData
                    : filterValues.values.merchantBranch
                }
                disabled={
                  selectLoadingData.values.isBranchesLoading ||
                  filterSelectData.values.merchantBranchesData?.length === 0 ||
                  filterValues.values.merchant?.length === 0
                }
                onChange={(value: string) =>
                  filterValues.setFieldValue('merchantBranch', value)
                }
                clearable={
                  !(
                    selectLoadingData.values.isBranchesLoading ||
                    filterSelectData.values.merchantBranchesData?.length ===
                      0 ||
                    filterValues.values.merchant?.length === 0
                  )
                }
                searchable
              />
            </ResponsiveCol>
          )}
          <ResponsiveCol>
            <Select
              label='Select POS'
              data={
                selectLoadingData.values.isPosLoading
                  ? [loadingData]
                  : !filterSelectData.values.posData //if posData is undefined
                  ? [noData]
                  : filterSelectData.values.posData?.length === 0 &&
                    filterValues.values.merchantBranch
                  ? [noData]
                  : filterSelectData.values.posData
              }
              value={
                selectLoadingData.values.isPosLoading
                  ? loadingData
                  : filterSelectData.values.posData?.length === 0 &&
                    filterValues.values.merchantBranch
                  ? noData
                  : filterValues.values.pos
              }
              disabled={
                selectLoadingData.values.isPosLoading ||
                filterSelectData.values.posData?.length === 0 ||
                filterValues.values.merchantBranch?.length === 0
              }
              onChange={(value: string) =>
                filterValues.setFieldValue('pos', value)
              }
              clearable={
                !(
                  selectLoadingData.values.isPosLoading ||
                  filterSelectData.values.posData?.length === 0 ||
                  filterValues.values.merchantBranch?.length === 0
                )
              }
              searchable
            />
          </ResponsiveCol>
        </Grid>
      </Container>
    </>
  );
}

export default ResellerPaymentLinkFilter;
